import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Calendar, Users, Zap } from "lucide-react";
import api from "@/lib/api";
import AdSlot from "@/components/AdSlot";

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data));
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const ongoing = events.filter((e) => e.status === "ongoing");
  const upcoming = events.filter((e) => e.status === "upcoming");
  const finished = events.filter((e) => e.status === "finished");

  return (
    <div className="pt-24 pb-20 min-h-screen" data-testid="events-page">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-14">
          <div className="text-xs uppercase tracking-widest text-[#FF0055] mb-3">Arena Kompetitif</div>
          <h1 className="font-heading text-3xl md:text-5xl font-black text-white tracking-tighter">
            EVENT &<br />TURNAMEN.
          </h1>
        </div>

        {/* Events Ad Slot (between title and content) */}
        {settings && settings.ad_slot_events && settings.ad_events_enabled !== false && (
          <div className="mb-10 py-4 border-y border-white/5" data-testid="ad-events-container">
            <AdSlot
              snippet={settings.ad_slot_events}
              publisherId={settings.adsense_publisher_id}
              label="Sponsored"
              slotKey="events"
            />
          </div>
        )}

        {/* Ongoing */}
        <section className="mb-16" data-testid="ongoing-section">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-2 h-2 bg-[#FF0055] rounded-full animate-pulse" />
            <h2 className="font-heading text-2xl font-bold text-white">TURNAMEN SEDANG BERLANGSUNG</h2>
          </div>

          {ongoing.length === 0 ? (
            <div className="border border-white/10 bg-[#0F0F11] p-10 text-center text-gray-500">
              Belum ada turnamen aktif saat ini.
            </div>
          ) : (
            <div className="space-y-6">
              {ongoing.map((e) => (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid={`ongoing-event-${e.id}`}
                  className="border border-[#FF0055]/40 bg-gradient-to-br from-[#FF0055]/5 to-transparent"
                >
                  <div className="p-6 md:p-8 border-b border-white/5">
                    <div className="flex items-start justify-between flex-wrap gap-4">
                      <div>
                        <div className="inline-flex items-center gap-2 px-2 py-1 bg-[#FF0055] text-white text-[10px] font-black uppercase tracking-widest mb-3">
                          <Zap size={10} /> LIVE
                        </div>
                        <h3 className="font-heading text-2xl md:text-3xl font-black text-white">{e.title}</h3>
                        <div className="text-sm text-gray-400 mt-1">{e.game}</div>
                      </div>
                      <div className="flex gap-6 text-sm">
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider">Prize Pool</div>
                          <div className="font-heading font-black text-[#00E5FF] text-lg mt-1">{e.prize_pool || "-"}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500 uppercase tracking-wider">Peserta</div>
                          <div className="font-heading font-black text-white text-lg mt-1">{e.participants?.length || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {e.matches && e.matches.length > 0 && (
                    <div className="p-6 md:p-8">
                      <div className="text-xs uppercase tracking-widest text-gray-400 mb-4">Bagan Pertandingan</div>
                      <div className="space-y-3">
                        {e.matches.map((m, i) => (
                          <div key={i} className="grid grid-cols-12 gap-3 items-center p-4 bg-black/40 border border-white/5">
                            <div className="col-span-2 text-xs uppercase tracking-widest text-gray-500">{m.round}</div>
                            <div className={`col-span-4 text-right font-semibold ${m.score1 > m.score2 ? "text-[#00E5FF]" : "text-white"}`}>
                              {m.player1}
                            </div>
                            <div className="col-span-2 text-center font-heading font-black text-xl text-white">
                              {m.score1} <span className="text-gray-500 text-sm">:</span> {m.score2}
                            </div>
                            <div className={`col-span-3 font-semibold ${m.score2 > m.score1 ? "text-[#00E5FF]" : "text-white"}`}>
                              {m.player2}
                            </div>
                            <div className="col-span-1 text-right">
                              <span className={`text-[10px] font-bold uppercase tracking-widest ${
                                m.status === "ongoing" ? "text-[#FF0055]" : m.status === "finished" ? "text-gray-500" : "text-yellow-500"
                              }`}>
                                {m.status || "TBD"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {e.participants && e.participants.length > 0 && (
                    <div className="p-6 md:p-8 border-t border-white/5">
                      <div className="text-xs uppercase tracking-widest text-gray-400 mb-3">Peserta</div>
                      <div className="flex flex-wrap gap-2">
                        {e.participants.map((p, i) => (
                          <span key={i} className="px-3 py-1.5 bg-white/5 border border-white/10 text-sm text-white">
                            {p}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* Upcoming */}
        <section className="mb-16" data-testid="upcoming-section">
          <div className="flex items-center gap-3 mb-6">
            <Calendar size={20} className="text-[#00E5FF]" />
            <h2 className="font-heading text-2xl font-bold text-white">EVENT AKAN DATANG</h2>
          </div>

          {upcoming.length === 0 ? (
            <div className="border border-white/10 bg-[#0F0F11] p-10 text-center text-gray-500">
              Belum ada event mendatang.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {upcoming.map((e, i) => (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`upcoming-event-${e.id}`}
                  className="border border-white/10 bg-[#0F0F11] overflow-hidden hover:border-[#00E5FF]/40 transition-colors"
                >
                  {e.poster_base64 && (
                    <div className="h-56 overflow-hidden">
                      <img src={e.poster_base64} alt={e.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="inline-block px-2 py-1 bg-[#00E5FF] text-black text-[10px] font-black uppercase tracking-widest">
                      Upcoming
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white mt-3">{e.title}</h3>
                    <div className="text-sm text-gray-400 mt-1">{e.game}</div>
                    <p className="text-sm text-gray-400 mt-3 leading-relaxed line-clamp-3">{e.description}</p>
                    <div className="mt-5 pt-5 border-t border-white/5 grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <div className="text-gray-500 uppercase tracking-wider">Tanggal</div>
                        <div className="text-white font-semibold mt-1">{e.event_date || "TBA"}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 uppercase tracking-wider">Prize</div>
                        <div className="text-[#00E5FF] font-bold mt-1">{e.prize_pool || "-"}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 uppercase tracking-wider">Entry</div>
                        <div className="text-white font-semibold mt-1">{e.entry_fee || "Gratis"}</div>
                      </div>
                    </div>
                    {e.registration_link && (
                      <a
                        href={e.registration_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`register-event-${e.id}`}
                        className="mt-5 inline-block w-full text-center bg-[#00E5FF] text-black font-bold py-3 uppercase tracking-wider text-sm hover:bg-white transition-colors"
                      >
                        Daftar Turnamen
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* Finished */}
        {finished.length > 0 && (
          <section data-testid="finished-section">
            <div className="flex items-center gap-3 mb-6">
              <Trophy size={20} className="text-gray-500" />
              <h2 className="font-heading text-2xl font-bold text-white">EVENT SELESAI</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {finished.map((e) => (
                <div key={e.id} className="border border-white/5 bg-[#0F0F11] p-5 opacity-70">
                  <div className="font-heading font-bold text-white">{e.title}</div>
                  <div className="text-xs text-gray-500 mt-1">{e.event_date}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
